"use client";

import { Location, Organizer, Tournament } from "@/types";
import { useTranslations } from "next-intl";
import { TournamentCardFantasy } from "./TournamentCardFantasy";

export interface TournamentWithDetails {
  tournament: Tournament;
  organizer: Organizer;
  locations?: Location[];
  teamCount?: number;
  playerCount?: number;
  matchCount?: number;
  freePlayerCount?: number;
}

interface TournamentGridProps {
  tournaments: TournamentWithDetails[];
  /** Show only active tournaments */
  activeOnly?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Loading state */
  isLoading?: boolean;
}

export function TournamentGrid({
  tournaments,
  activeOnly = true,
  emptyMessage,
  isLoading = false,
}: TournamentGridProps) {
  const t = useTranslations();
  const filteredTournaments = activeOnly
    ? tournaments.filter((t) => t.tournament.isActive)
    : tournaments;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="tc animate-pulse"
            aria-hidden="true"
            style={{ minHeight: 520 }}
          />
        ))}
      </div>
    );
  }

  if (filteredTournaments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-card)] px-8 py-16">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--border-subtle)]">
          <svg
            className="h-8 w-8 text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <p className="text-center font-spectral text-muted">
          {emptyMessage || t("tournaments.noTournamentsAvailable")}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Shared SVG sprite for .tc corners and divider emblem */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
      >
        <defs>
          <symbol id="tc-cn" viewBox="0 0 16 16">
            <path
              d="M2 2 L8 2 M2 2 L2 8 M5 2 Q5 5 8 5 Q8 8 5 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.1"
              strokeLinecap="round"
            />
            <circle cx="2.5" cy="2.5" r="0.9" fill="currentColor" />
          </symbol>
          <symbol id="tc-tower-sm" viewBox="0 0 24 24">
            <path
              d="M5 22 L5 10 L8 10 L8 7 L12 4 L16 7 L16 10 L19 10 L19 22 Z M9 22 L9 16 L15 16 L15 22"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </symbol>
        </defs>
      </svg>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredTournaments.map(
          ({
            tournament,
            organizer,
            locations,
            playerCount,
            teamCount,
            matchCount,
            freePlayerCount,
          }) => (
            <TournamentCardFantasy
              key={tournament.id}
              tournament={tournament}
              organizer={organizer}
              locations={locations}
              playerCount={playerCount}
              teamCount={teamCount}
              matchCount={matchCount}
              freePlayerCount={freePlayerCount}
            />
          ),
        )}
      </div>
    </>
  );
}
